import React, { createContext, useCallback, useContext, useState } from "react";
import Swal from "sweetalert2";
import {
  child,
  get,
  push as firebasePush,
  ref,
  remove,
  set,
} from "firebase/database";
import { database } from "../config/firebase";

export interface BaseContentItem {
  uid: string;
  title: string;
  description: string;
  createdAt: string;
}

export interface NewsItem extends BaseContentItem {
  published: boolean;
}

export interface NotificationItem extends BaseContentItem {
  audience: string;
}

export interface SuggestionItem extends BaseContentItem {
  fromUser?: string;
}

export interface FaqItem extends BaseContentItem {
  answer: string;
}

interface ContentContextData {
  loading: boolean;
  listNews(): Promise<NewsItem[]>;
  saveNews(
    item: Omit<NewsItem, "uid" | "createdAt"> & {
      uid?: string;
      createdAt?: string;
    }
  ): Promise<void>;
  removeNews(uid: string): Promise<void>;

  listNotifications(): Promise<NotificationItem[]>;
  saveNotification(
    item: Omit<NotificationItem, "uid" | "createdAt"> & {
      uid?: string;
      createdAt?: string;
    }
  ): Promise<void>;
  removeNotification(uid: string): Promise<void>;

  listSuggestions(): Promise<SuggestionItem[]>;
  getSuggestion(uid: string): Promise<SuggestionItem | null>;

  listFaq(): Promise<FaqItem[]>;
  saveFaq(
    item: Omit<FaqItem, "uid" | "createdAt"> & {
      uid?: string;
      createdAt?: string;
    }
  ): Promise<void>;
  removeFaq(uid: string): Promise<void>;
}

const ContentContext = createContext<ContentContextData>(
  {} as ContentContextData
);

const ContentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);

  const listCollection = useCallback(async <T,>(path: string) => {
    const items: T[] = [];
    const snapshot = await get(child(ref(database), path));
    snapshot.forEach((snap) => {
      items.push(snap.val() as T);
    });
    return items;
  }, []);

  const listNews = useCallback(async () => {
    try {
      return await listCollection<NewsItem>("news");
    } catch (err) {
      throw new Error("Houve um erro ao carregar as notícias");
    }
  }, [listCollection]);

  const saveNews = useCallback(
    async (
      item: Omit<NewsItem, "uid" | "createdAt"> & {
        uid?: string;
        createdAt?: string;
      }
    ) => {
      setLoading(true);
      Swal.fire({
        title: "Salvando notícia...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          try {
            const isEdit = !!item.uid;
            const newsRef = isEdit
              ? child(ref(database), `news/${item.uid}`)
              : firebasePush(child(ref(database), "news"));
            let uid: string;
            if (isEdit) {
              if (!item.uid) throw new Error("UID da notícia ausente");
              uid = item.uid;
            } else {
              const k = newsRef.key;
              if (!k) throw new Error("Falha ao gerar chave da notícia");
              uid = k;
            }

            const createdAt = item.createdAt ?? new Date().toISOString();

            await set(newsRef, {
              uid,
              title: item.title,
              description: item.description,
              published: item.published ?? false,
              createdAt,
            });

            Swal.close();
            await Swal.fire({
              icon: "success",
              title: "Notícia salva",
              text: "Notícia salva com sucesso!",
              showConfirmButton: false,
              timer: 1600,
            });
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.",
            });
          } finally {
            setLoading(false);
          }
        },
      });
    },
    []
  );

  const removeNews = useCallback(async (uid: string) => {
    await remove(child(ref(database), `news/${uid}`));
  }, []);

  const listNotifications = useCallback(async () => {
    try {
      return await listCollection<NotificationItem>("notifications");
    } catch (err) {
      throw new Error("Houve um erro ao carregar as notificações");
    }
  }, [listCollection]);

  const saveNotification = useCallback(
    async (
      item: Omit<NotificationItem, "uid" | "createdAt"> & {
        uid?: string;
        createdAt?: string;
      }
    ) => {
      setLoading(true);
      Swal.fire({
        title: "Salvando notificação...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          try {
            const isEdit = !!item.uid;
            const notifRef = isEdit
              ? child(ref(database), `notifications/${item.uid}`)
              : firebasePush(child(ref(database), "notifications"));
            let uid: string;
            if (isEdit) {
              if (!item.uid) throw new Error("UID da notificação ausente");
              uid = item.uid;
            } else {
              const k = notifRef.key;
              if (!k) throw new Error("Falha ao gerar chave da notificação");
              uid = k;
            }

            const createdAt = item.createdAt ?? new Date().toISOString();

            await set(notifRef, {
              uid,
              title: item.title,
              description: item.description,
              audience: item.audience || "all",
              createdAt,
            });

            Swal.close();
            await Swal.fire({
              icon: "success",
              title: "Notificação salva",
              text: "Notificação salva com sucesso!",
              showConfirmButton: false,
              timer: 1600,
            });
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.",
            });
          } finally {
            setLoading(false);
          }
        },
      });
    },
    []
  );

  const removeNotification = useCallback(async (uid: string) => {
    await remove(child(ref(database), `notifications/${uid}`));
  }, []);

  const listSuggestions = useCallback(async () => {
    try {
      return await listCollection<SuggestionItem>("suggestions");
    } catch (err) {
      throw new Error("Houve um erro ao carregar as sugestões");
    }
  }, [listCollection]);

  const getSuggestion = useCallback(async (uid: string) => {
    const snapshot = await get(child(ref(database), `suggestions/${uid}`));
    if (!snapshot.exists()) return null;
    return snapshot.val() as SuggestionItem;
  }, []);

  const listFaq = useCallback(async () => {
    try {
      return await listCollection<FaqItem>("faq");
    } catch (err) {
      throw new Error("Houve um erro ao carregar o FAQ");
    }
  }, [listCollection]);

  const saveFaq = useCallback(
    async (
      item: Omit<FaqItem, "uid" | "createdAt"> & {
        uid?: string;
        createdAt?: string;
      }
    ) => {
      setLoading(true);
      Swal.fire({
        title: "Salvando FAQ...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          try {
            const isEdit = !!item.uid;
            const faqRef = isEdit
              ? child(ref(database), `faq/${item.uid}`)
              : firebasePush(child(ref(database), "faq"));
            let uid: string;
            if (isEdit) {
              if (!item.uid) throw new Error("UID do FAQ ausente");
              uid = item.uid;
            } else {
              const k = faqRef.key;
              if (!k) throw new Error("Falha ao gerar chave do FAQ");
              uid = k;
            }

            const createdAt = item.createdAt ?? new Date().toISOString();

            await set(faqRef, {
              uid,
              title: item.title,
              description: item.description,
              answer: item.answer,
              createdAt,
            });

            Swal.close();
            await Swal.fire({
              icon: "success",
              title: "FAQ salvo",
              text: "FAQ salvo com sucesso!",
              showConfirmButton: false,
              timer: 1600,
            });
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.",
            });
          } finally {
            setLoading(false);
          }
        },
      });
    },
    []
  );

  const removeFaq = useCallback(async (uid: string) => {
    await remove(child(ref(database), `faq/${uid}`));
  }, []);

  const providerValue = React.useMemo(
    () => ({
      loading,
      listNews,
      saveNews,
      removeNews,
      listNotifications,
      saveNotification,
      removeNotification,
      listSuggestions,
      getSuggestion,
      listFaq,
      saveFaq,
      removeFaq,
    }),
    [
      loading,
      listNews,
      saveNews,
      removeNews,
      listNotifications,
      saveNotification,
      removeNotification,
      listSuggestions,
      getSuggestion,
      listFaq,
      saveFaq,
      removeFaq,
    ]
  );

  return (
    <ContentContext.Provider value={providerValue}>
      {children}
    </ContentContext.Provider>
  );
};

function useContent(): ContentContextData {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error("useContent must be used within a ContentProvider");
  }
  return context;
}

export { ContentProvider, useContent };
